//@ts-check
import moment from 'moment';
import { DateTime } from 'luxon';
import { invalidWeekdays } from '../components/views/digital-vault-alerts/alert-details';

/**
 * @author Pedro Antonio
 * @description Utility class for managing digital vault alerts
 * @class DigitalVaultAlertUtils
 */
class DigitalVaultAlertUtils {
    /**
     * Creates default initial data for alert form
     * @returns {import('../types/alerts.types').AlertState}
     */
    static createDefaultInitialData() {
        const TOMORROW = DateTime.now().plus({ days: 1 }).startOf('day').toJSDate()

        return {
            name: '',
            alertDocumentVariables: [],
            documentId: '',
            emailList: '',
            description: '',
            includeURL: false,
            startDate: TOMORROW,
            type: '1',
            id: undefined,
            endDate: TOMORROW,
            ocurrences: 1,
            recurrenceType: 1,
            dailyRecurrence: 1,
            weeklyRecurrence: {
                times: 1,
                weekDays: [],
            },
            monthlyRecurrence: {
                type: '1',
                byDay: {
                    day: 1,
                    times: 1,
                },
                byOrdinalNumber: {
                    ordinalNumber: 1,
                    weekDays: 1,
                    times: 1
                }
            },
            yearlyRecurrence: {
                type: '1',
                times: 1,
                byDay: {
                    day: 0,
                    month: 0,
                },
                byOrdinalNumber: {
                    ordinalNumber: 1,
                    weekDays: /** @typedef {number[]} */ 1,
                    month: 0
                }
            },
            documentType: {
                id: 0,
                name: 'Selecciona un dato',
                comments: ''
            },
        };
    }

    /**
     * Converts alert data to form format
     * @param {import('../types/alerts.types').AlertData} alert - Alert data from API
     * @returns {import('../types/alerts.types').AlertState}
     */
    static setDataToForm(alert) {
        let copyAlert = { ...alert }
        const emailList = (copyAlert?.emailList?.values || []).join(',');
        const defaultData = this.createDefaultInitialData();

        if (copyAlert?.weekDays?.values?.length) {
            defaultData.weeklyRecurrence = {
                times: copyAlert.times || 1,
                weekDays: copyAlert.weekDays.values
            };
        }

        if (copyAlert?.day && copyAlert?.alertTypeRecurrence?.key === 'Mensual') {
            defaultData.monthlyRecurrence = {
                type: '1',
                byDay: {
                    day: copyAlert.day,
                    times: copyAlert.times || 1,
                },
                byOrdinalNumber: {
                    ordinalNumber: 1,
                    weekDays: 1,
                    times: 1
                }
            };
        }
        else if (copyAlert?.ordinalNumber && copyAlert?.weekDays?.values?.length && copyAlert?.alertTypeRecurrence?.key === 'Mensual') {
            defaultData.monthlyRecurrence = {
                type: '2',
                byOrdinalNumber: {
                    ordinalNumber: copyAlert.ordinalNumber,
                    weekDays: copyAlert.weekDays.values[0],
                    times: copyAlert.times || 1
                },
                byDay: {
                    day: 1,
                    times: 1,
                }
            };
        }
        else if (copyAlert?.ordinalNumber && copyAlert?.weekDays?.values?.length && copyAlert?.month && copyAlert?.alertTypeRecurrence?.key === 'Anual') {
            defaultData.yearlyRecurrence = {
                type: '2',
                times: copyAlert.times || 1,
                byOrdinalNumber: {
                    ordinalNumber: copyAlert.ordinalNumber,
                    weekDays: copyAlert.weekDays.values[0],
                    month: copyAlert.month
                },
                byDay: {
                    day: 1,
                    month: 0,
                }
            };
        }
        else if (copyAlert?.ordinalNumber && copyAlert?.weekDays?.values?.length && copyAlert?.alertTypeRecurrence?.key === 'Anual') {
            defaultData.yearlyRecurrence = {
                type: '2',
                times: copyAlert.times || 1,
                byOrdinalNumber: {
                    ordinalNumber: copyAlert.ordinalNumber,
                    weekDays: copyAlert.weekDays.values[0],
                    month: 0
                },
                byDay: {
                    day: 0,
                    month: 0,
                }
            };
        }

        if (copyAlert?.day && copyAlert?.month && copyAlert?.alertTypeRecurrence?.key === 'Anual') {
            defaultData.yearlyRecurrence = {
                type: '1',
                times: copyAlert.times || 1,
                byDay: {
                    day: copyAlert.day,
                    month: copyAlert.month
                },
                byOrdinalNumber: {
                    ordinalNumber: 0,
                    weekDays: 0,
                    month: 0
                }
            };
        }
        return {
            ...defaultData,
            ...alert,
            id: copyAlert.id,
            emailList,
            dailyRecurrence: copyAlert.times || 1,
            documentId: copyAlert.documentId,
            startDate: moment(copyAlert?.startDate).toDate(),
            endDate: copyAlert?.endDate ? moment(copyAlert.endDate).toDate() : new Date(),
            recurrenceType: copyAlert?.alertTypeRecurrenceId || 1,
            ocurrences: copyAlert.ocurrences || 1,
            type: (copyAlert?.ocurrences && !copyAlert?.endDate) ? '2' : '1'
        };
    }

    /**
     * Calculates grid height based on number of alerts
     * @param {number} alertsCount - Number of alerts
     * @returns {string}
     */
    static calculateGridHeight(alertsCount) {
        if (alertsCount <= 1) return '110px';
        if (alertsCount === 2) return '180px';
        if (alertsCount === 3) return '280px';
        return '320px';
    }

    /**
     * Validates email format
     * @param {string} email - Email to validate
     * @returns {boolean}
     */
    static isValidEmail(email) {
        const emailRegex = /^[\w-.]+@([\w-]+.)+[\w-]{2,10}$/;
        return emailRegex.test(email);
    }

    /**
     * Validates basic information step
     * @param {import('../types/alerts.types').AlertState} data - Form data
     * @returns {string[]} Array of invalid field names
     */
    static validateBasicInfo(data) {
        const invalidFields = [];
        const { name, emailList, description } = data

        if (!name) invalidFields.push('name');
        if (!description || !String(description).trim().length) invalidFields.push('description')
        if (!emailList.length) invalidFields.push('emailList');

        const emailsList = emailList.split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0);

        for (const email of emailsList) {
            if (!this.isValidEmail(email)) {
                invalidFields.push('emailList');
                break;
            }
        }

        return invalidFields;
    }

    /**
     * Validates recurrence configuration step
     * @param {import('../types/alerts.types').AlertState} data - Form data
     * @param {any} documentData - Document data
     * @returns {string[]} Array of invalid field names
     */
    static validateRecurrenceConfig(data, documentData) {
        const invalidFields = [];
        const {
            startDate, endDate, ocurrences, recurrenceType,
            weeklyRecurrence, monthlyRecurrence, yearlyRecurrence, type
        } = data;

        // Validate dates
        if ((!documentData || !documentData.id) || moment(startDate).isSameOrBefore(moment().startOf('day'), 'day')) {
            invalidFields.push('startDate');
        }

        // Validate based on type (1 = finish date, 2 = times/occurrences)
        if (type === '1') {
            // Type 1: Validate end date
            if (!endDate) {
                invalidFields.push('endDate');
            } else if (moment(endDate).isBefore(moment(startDate), 'day')) {
                invalidFields.push('endDate');
            }
        } else if (type === '2') {
            // Type 2: Validate occurrences
            if (!ocurrences || ocurrences < 1) {
                invalidFields.push('ocurrences');
            }
        }

        // Get times based on recurrence type
        const times = this.getTimesForRecurrenceType(data, recurrenceType);
        if (times === undefined || times < 1) {
            invalidFields.push('times');
        }

        // Validate specific recurrence types
        switch (recurrenceType) {
            case 1: // DAILY
                if (times > 999) invalidFields.push('times');
                break;
            case 2: // WEEKLY
                if (times > 99) invalidFields.push('times');
                if (invalidWeekdays(weeklyRecurrence.weekDays)) invalidFields.push('weekDays');
                break;
            case 3: // MONTHLY
                if (times > 36) invalidFields.push('times');
                if (monthlyRecurrence.type === '1') {
                    const day = monthlyRecurrence.byDay.day;
                    if (day !== undefined && (day < 0 || day > 31)) {
                        invalidFields.push('day');
                    }
                }
                break;
            case 4: // YEARLY
                if (yearlyRecurrence.type === '1') {
                    if (yearlyRecurrence.byDay.month < 0 || yearlyRecurrence.byDay.month === undefined || yearlyRecurrence.byDay.month === null) {
                        invalidFields.push('month');
                    }
                    const isValid = moment(
                        `${yearlyRecurrence.byDay.month + 1}-${yearlyRecurrence.byDay.day}`,
                        'M-D',
                        true
                    ).isValid();

                    if (!isValid && !(yearlyRecurrence.byDay.month === 1 && yearlyRecurrence.byDay.day === 29)) {
                        invalidFields.push('day');
                    }
                }
                break;
            default:
                break;
        }

        return invalidFields;
    }

    /**
     * @param {import('../types/alerts.types').AlertState} data 
     * @returns {string[]} Array of invalid field names
     */
    static validateCustomData(data) {
        let invalidFields = []
        const { documentType } = data;
        if (documentType && !documentType.id && documentType?.comments?.trim()) {
            invalidFields.push('documentType');
        }

        return invalidFields;
    }

    /**
     * @param {import('../types/alerts.types').AlertState} data 
     * @returns {string[]} Array of invalid field names
     */
    static validateDataToAppend(data) {
        let invalidFields = []
        const { documentType } = data;
        if (!documentType || documentType.id === 0) {
            invalidFields.push('documentType');
        }

        return invalidFields;
    }

    /**
     * Gets times value based on recurrence type
     * @param {import('../types/alerts.types').AlertState} data - Form data
     * @param {number} recurrenceType - Type of recurrence
     * @returns {number}
     */
    static getTimesForRecurrenceType(data, recurrenceType) {
        const { dailyRecurrence, weeklyRecurrence, monthlyRecurrence, yearlyRecurrence } = data;

        switch (recurrenceType) {
            case 1: // DAILY
                return dailyRecurrence;
            case 2: // WEEKLY
                return weeklyRecurrence.times;
            case 3: // MONTHLY
                return monthlyRecurrence.type === '1'
                    ? monthlyRecurrence.byDay.times
                    : monthlyRecurrence.byOrdinalNumber.times;
            case 4: // YEARLY
                return yearlyRecurrence.times;
            default:
                return 0;
        }
    }

    /**
     * Validates form data based on current step
     * @param {import('../types/alerts.types').AlertState} data - Form data
     * @param {number} currentStep - Current validation step
     * @param {any} documentData - Document data
     * @returns {{ isValid: boolean, invalidFields: string[] }}
     */
    static validateData(data, currentStep, documentData) {
        /** @type {string[]} */
        let invalidFields = [];

        switch (currentStep) {
            case 0:
                invalidFields = this.validateBasicInfo(data);
                break;
            case 1:
                invalidFields = this.validateRecurrenceConfig(data, documentData);
                break;
            case 2:
                invalidFields = this.validateCustomData(data);
                break;
            default:
                break;
        }

        return {
            isValid: invalidFields.length === 0,
            invalidFields
        };
    }

    /**
     * Updates daily recurrence in form data
     * @param {import('../types/alerts.types').AlertState} currentData - Current form data
     * @param {any} value - New daily recurrence value
     * @returns {import('../types/alerts.types').AlertState}
     */
    static updateDailyRecurrence(currentData, value) {
        return {
            ...currentData,
            dailyRecurrence: value
        };
    }

    /**
     * Updates weekly recurrence in form data
     * @param {import('../types/alerts.types').AlertState} currentData - Current form data
     * @param {React.ChangeEvent<HTMLInputElement>} e - Field to update ('times' or day number)
     * @returns {import('../types/alerts.types').AlertState}
     */
    static updateWeeklyRecurrence(currentData, e) {
        const id = e.target.id;
        if (id === 'rec2-times') {
            if (e.target.value.length > 2) return currentData;
            return {
                ...currentData,
                weeklyRecurrence: {
                    ...currentData.weeklyRecurrence,
                    times: +e.target.value
                }
            };
        } else {
            const day = parseInt(id.slice(-1));
            if (e.target.checked) {
                return {
                    ...currentData,
                    weeklyRecurrence: {
                        ...currentData.weeklyRecurrence,
                        weekDays: [...currentData.weeklyRecurrence.weekDays, day]
                    }
                };
            } else {
                return {
                    ...currentData,
                    weeklyRecurrence: {
                        ...currentData.weeklyRecurrence,
                        weekDays: currentData.weeklyRecurrence.weekDays.filter(d => d !== day)
                    }
                };
            }
        }
    }

    /**
     * Updates monthly recurrence in form data
     * @param {import('../types/alerts.types').AlertState} currentData - Current form data
     * @param {string} type - Recurrence type ('1' or '2')
     * @param {string} field - Field to update
     * @param {any} value - New value
     * @returns {import('../types/alerts.types').AlertState}
     */
    static updateMonthlyRecurrence(currentData, type, field, value) {
        if (field === 'type') {
            return {
                ...currentData,
                monthlyRecurrence: {
                    ...currentData.monthlyRecurrence,
                    type: value
                }
            };
        }

        const updates = {
            byDay: type === '1'
                ? { ...currentData.monthlyRecurrence.byDay, [field]: value }
                : currentData.monthlyRecurrence.byDay,
            byOrdinalNumber: type === '2'
                ? { ...currentData.monthlyRecurrence.byOrdinalNumber, [field]: value }
                : currentData.monthlyRecurrence.byOrdinalNumber
        };

        return {
            ...currentData,
            monthlyRecurrence: {
                ...currentData.monthlyRecurrence,
                ...updates
            }
        };
    }

    /**
     * Updates yearly recurrence in form data
     * @param {import('../types/alerts.types').AlertState} currentData - Current form data
     * @param {string} type - Recurrence type ('1' or '2')
     * @param {string} field - Field to update
     * @param {any} value - New value
     * @returns {import('../types/alerts.types').AlertState}
     */
    static updateYearlyRecurrence(currentData, type, field, value) {
        if (field === 'type') {
            return {
                ...currentData,
                yearlyRecurrence: {
                    ...currentData.yearlyRecurrence,
                    type: value
                }
            };
        }

        if (field === 'times') {
            return {
                ...currentData,
                yearlyRecurrence: {
                    ...currentData.yearlyRecurrence,
                    times: value
                }
            };
        }

        const updates = {
            byDay: type === '1'
                ? { ...currentData.yearlyRecurrence.byDay, [field]: value }
                : currentData.yearlyRecurrence.byDay,
            byOrdinalNumber: type === '2'
                ? { ...currentData.yearlyRecurrence.byOrdinalNumber, [field]: value }
                : currentData.yearlyRecurrence.byOrdinalNumber
        };

        return {
            ...currentData,
            yearlyRecurrence: {
                ...currentData.yearlyRecurrence,
                ...updates
            }
        };
    }

    /**
     * Prepares alert data for backend submission
     * @param {import('../types/alerts.types').AlertState} alert - Parameters object
     * @param {string} documentId - Document ID
     */
    static prepareAlertDataForBackend(alert, documentId) {
        const emailList = alert.emailList.trim().split(/\s*[;,]\s*/);

        /** @type {import('../types/alerts.types').AlertPost} */
        let alertData = {
            name: alert.name.trim(),
            description: alert.description.trim(),
            alertDocumentVariables: (alert.alertDocumentVariables || []).map(dt => ({
                documentVariableId: dt.documentVariableId,
                comments: dt.comments || ""
            })),
            documentId: documentId,
            startDate: DateTime.fromJSDate(alert.startDate).startOf('day').toJSDate(),
            emailList,
            includeURL: alert.includeURL,
            recurrenceType: alert.recurrenceType,
            endDate: alert.endDate ? DateTime.fromJSDate(alert.endDate).startOf('day').toJSDate() : undefined,
            ocurrences: alert.ocurrences,
            alertTypeRecurrenceId: alert.recurrenceType,
            times: 1,
        };

        // Add ID and documentId for existing alerts
        if (alert && alert.id) {
            alertData.id = alert.id;
            alertData.documentId = alert.documentId;
        }

        // Handle range type (end date or occurrences)
        if (alert.type === '1') {
            alertData.endDate = alert.endDate;
            delete alertData.ocurrences
        } else if (alert.type === '2') {
            alertData.ocurrences = alert.ocurrences;
            delete alertData.endDate
        }

        // Handle recurrence data based on type
        switch (alert.recurrenceType) {
            case 1: // Daily
                alertData.times = +alert.dailyRecurrence;
                break;
            case 2: // Weekly
                alertData.times = alert.weeklyRecurrence.times;
                alertData.weekDays = alert.weeklyRecurrence.weekDays;
                break;
            case 3: // Monthly
                if (alert.monthlyRecurrence.type === '1') {
                    alertData.day = alert.monthlyRecurrence.byDay.day;
                    alertData.times = alert.monthlyRecurrence.byDay.times;
                } else if (alert.monthlyRecurrence.type === '2') {
                    alertData.ordinalNumber = alert.monthlyRecurrence.byOrdinalNumber.ordinalNumber;
                    alertData.weekDays = [alert.monthlyRecurrence.byOrdinalNumber.weekDays];
                    alertData.times = alert.monthlyRecurrence.byOrdinalNumber.times;
                }
                break;
            case 4: // Yearly
                alertData.times = alert.yearlyRecurrence.times;
                if (alert.yearlyRecurrence.type === '1') {
                    alertData.day = alert.yearlyRecurrence.byDay.day;
                    alertData.month = alert.yearlyRecurrence.byDay.month;
                } else if (alert.yearlyRecurrence.type === '2') {
                    alertData.ordinalNumber = alert.yearlyRecurrence.byOrdinalNumber.ordinalNumber;
                    alertData.weekDays = [alert.yearlyRecurrence.byOrdinalNumber.weekDays];
                    alertData.month = alert.yearlyRecurrence.byOrdinalNumber.month;
                }
                break;
            default:
                break;
        }

        return alertData;
    }
}

export default DigitalVaultAlertUtils;